/**
 * 产品管理服务 V2
 * 与后端产品管理V2 API进行交互
 */
import { apiRequest } from '@/utils/api'
import api from '@/utils/api'  // 直接导入 api 实例用于文件上传

const productService = {
  /**
   * 获取产品列表
   * @param {Object} params - 查询参数
   * @param {string} params.search - 搜索关键词
   * @param {string} params.brand - 品牌筛选
   * @param {boolean} params.is_active - 状态筛选
   * @param {number} params.page - 页码
   * @param {number} params.page_size - 每页数量
   * @param {string} params.ordering - 排序字段
   * @returns {Promise<Object>} API响应
   */
  async getProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      // 添加查询参数
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const url = `/api/products/v2/products/${queryString ? `?${queryString}` : ''}`
      
      const response = await apiRequest.get(url)
      return {
        success: true,
        data: response.data,
        results: response.data.results || [],
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous
      }
    } catch (error) {
      console.error('获取产品列表失败:', error)
      throw error
    }
  },

  /**
   * 获取产品详情
   * @param {number} productId - 产品ID
   * @returns {Promise<Object>} 产品详情
   */
  async getProduct(productId) {
    try {
      const response = await apiRequest.get(`/api/products/v2/products/${productId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取产品详情失败:', error)
      throw error
    }
  },

  /**
   * 创建产品
   * @param {Object} productData - 产品数据
   * @param {string} productData.name - 产品名称
   * @param {string} productData.code - 产品编号
   * @param {string} productData.brand - 品牌
   * @param {string} productData.description - 描述
   * @param {File} productData.image - 产品图片
   * @param {boolean} productData.is_active - 是否启用
   * @returns {Promise<Object>} 创建结果
   */
  async createProduct(productData) {
    try {
      // 如果已经是FormData，直接使用；否则构建FormData
      let formData
      if (productData instanceof FormData) {
        formData = productData
      } else {
        formData = new FormData()
        Object.keys(productData).forEach(key => {
          if (productData[key] !== undefined && productData[key] !== null) {
            if (key === 'image' && productData[key] instanceof File) {
              formData.append(key, productData[key], productData[key].name)
            } else {
              formData.append(key, productData[key])
            }
          }
        })
      }
      
      // 直接使用 api 实例，绕过 apiRequest 包装
      const response = await api.post('/api/products/v2/products/', formData, {
        headers: {
          'Content-Type': undefined  // 让 Axios 自动设置
        }
      })
      
      return {
        success: true,
        data: response.data,
        message: '产品创建成功'
      }
    } catch (error) {
      console.error('创建产品失败:', error)
      throw error
    }
  },

  /**
   * 更新产品
   * @param {number} productId - 产品ID
   * @param {Object} productData - 更新的产品数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateProduct(productId, productData) {
    try {
      // 如果已经是FormData，直接使用；否则构建FormData
      let formData
      if (productData instanceof FormData) {
        formData = productData
      } else {
        formData = new FormData()
        Object.keys(productData).forEach(key => {
          if (productData[key] !== undefined && productData[key] !== null) {
            if (key === 'image' && productData[key] instanceof File) {
              formData.append(key, productData[key], productData[key].name)
            } else {
              formData.append(key, productData[key])
            }
          }
        })
      }
      
      // 直接使用 api 实例，绕过 apiRequest 包装
      const response = await apiRequest.put(`/api/products/v2/products/${productId}/`, formData, {
        headers: {
          'Content-Type': undefined
        }
      })
      
      return {
        success: true,
        data: response.data,
        message: '产品更新成功'
      }
    } catch (error) {
      console.error('更新产品失败:', error)
      throw error
    }
  },

  /**
   * 删除产品
   * @param {number} productId - 产品ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteProduct(productId) {
    try {
      await apiRequest.delete(`/api/products/v2/products/${productId}/`)
      return {
        success: true,
        message: '产品删除成功'
      }
    } catch (error) {
      console.error('删除产品失败:', error)
      throw error
    }
  },

  /**
   * 切换产品状态
   * @param {number} productId - 产品ID
   * @returns {Promise<Object>} 操作结果
   */
  async toggleProductStatus(productId) {
    try {
      const response = await apiRequest.post(`/api/products/v2/products/${productId}/toggle_status/`)
      return {
        success: true,
        data: response.data,
        message: response.data.message || '状态切换成功'
      }
    } catch (error) {
      console.error('切换产品状态失败:', error)
      throw error
    }
  },

  /**
   * 批量操作产品
   * @param {Array<number>} productIds - 产品ID列表
   * @param {string} action - 操作类型 ('activate', 'deactivate', 'delete')
   * @returns {Promise<Object>} 操作结果
   */
  async bulkActionProducts(productIds, action) {
    try {
      const response = await apiRequest.post('/api/products/v2/products/bulk_action/', {
        product_ids: productIds,
        action: action
      })
      
      return {
        success: true,
        data: response.data,
        message: response.data.message || '批量操作成功'
      }
    } catch (error) {
      console.error('批量操作产品失败:', error)
      throw error
    }
  },

  /**
   * 获取产品统计信息
   * @returns {Promise<Object>} 统计信息
   */
  async getProductStatistics() {
    try {
      const response = await apiRequest.get('/api/products/v2/products/statistics/')
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取产品统计失败:', error)
      throw error
    }
  },

  /**
   * 获取品牌列表
   * @returns {Promise<Object>} 品牌列表
   */
  async getBrands() {
    try {
      const response = await apiRequest.get('/api/products/v2/products/brands/')
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取品牌列表失败:', error)
      throw error
    }
  },

  /**
   * 获取产品分组列表（用于筛选）
   * @returns {Promise<Object>} 分组列表
   */
  async getGroups() {
    try {
      const response = await apiRequest.get('/api/products/v2/products/groups/')
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取产品分组列表失败:', error)
      throw error
    }
  },

  /**
   * 搜索产品建议（自动完成）
   * @param {string} query - 搜索关键词
   * @param {number} limit - 限制数量
   * @returns {Promise<Object>} 搜索建议
   */
  async getProductSuggestions(query, limit = 10) {
    try {
      const params = new URLSearchParams({
        search: query,
        page_size: limit
      })
      
      const response = await apiRequest.get(`/api/products/v2/products/?${params.toString()}`)
      return {
        success: true,
        data: response.data.results || []
      }
    } catch (error) {
      console.error('获取产品建议失败:', error)
      throw error
    }
  },

  /**
   * 上传产品图片
   * @param {File} imageFile - 图片文件
   * @param {number} productId - 产品ID（可选，用于更新现有产品）
   * @returns {Promise<Object>} 上传结果
   */
  async uploadProductImage(imageFile, productId = null) {
    try {
      const formData = new FormData()
      formData.append('image', imageFile)
      
      let url, method
      if (productId) {
        // 更新现有产品的图片
        url = `/api/products/v2/products/${productId}/`
        method = 'patch'
      } else {
        // 临时上传（用于预览）
        url = '/api/products/v2/products/upload_image/'
        method = 'post'
      }
      
      const response = await apiRequest[method](url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      return {
        success: true,
        data: response.data,
        message: '图片上传成功'
      }
    } catch (error) {
      console.error('上传产品图片失败:', error)
      throw error
    }
  }
}

/**
 * 产品分组管理服务 V2
 */
export const productGroupService = {
  /**
   * 获取产品分组列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} API响应
   */
  async getProductGroups(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const url = `/api/products/v2/product-groups/${queryString ? `?${queryString}` : ''}`
      
      const response = await apiRequest.get(url)
      return {
        success: true,
        data: response.data,
        results: response.data.results || [],
        count: response.data.count || 0
      }
    } catch (error) {
      console.error('获取产品分组列表失败:', error)
      throw error
    }
  },

  /**
   * 获取产品分组详情
   * @param {number} groupId - 分组ID
   * @returns {Promise<Object>} 分组详情
   */
  async getProductGroup(groupId) {
    try {
      const response = await apiRequest.get(`/api/products/v2/product-groups/${groupId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取产品分组详情失败:', error)
      throw error
    }
  },

  /**
   * 创建产品分组
   * @param {Object} groupData - 分组数据
   * @returns {Promise<Object>} 创建结果
   */
  async createProductGroup(groupData) {
    try {
      const response = await apiRequest.post('/api/products/v2/product-groups/', groupData)
      return {
        success: true,
        data: response.data,
        message: '产品分组创建成功'
      }
    } catch (error) {
      console.error('创建产品分组失败:', error)
      throw error
    }
  },

  /**
   * 更新产品分组
   * @param {number} groupId - 分组ID
   * @param {Object} groupData - 更新的分组数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateProductGroup(groupId, groupData) {
    try {
      const response = await apiRequest.put(`/api/products/v2/product-groups/${groupId}/`, groupData)
      return {
        success: true,
        data: response.data,
        message: '产品分组更新成功'
      }
    } catch (error) {
      console.error('更新产品分组失败:', error)
      throw error
    }
  },

  /**
   * 删除产品分组
   * @param {number} groupId - 分组ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteProductGroup(groupId) {
    try {
      await apiRequest.delete(`/api/products/v2/product-groups/${groupId}/`)
      return {
        success: true,
        message: '产品分组删除成功'
      }
    } catch (error) {
      console.error('删除产品分组失败:', error)
      throw error
    }
  },

  /**
   * 批量排序产品分组
   * @param {Array<number>} groupIds - 分组ID列表（按新顺序排列）
   * @returns {Promise<Object>} 操作结果
   */
  async sortProductGroups(groupIds) {
    try {
      const response = await apiRequest.post('/api/products/v2/product-groups/sort_groups/', {
        group_ids: groupIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message || '分组排序更新成功'
      }
    } catch (error) {
      console.error('产品分组排序失败:', error)
      throw error
    }
  }
}

/**
 * 产品自定义字段管理服务 V2
 */
export const productCustomFieldService = {
  /**
   * 获取产品自定义字段列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} API响应
   */
  async getCustomFields(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const url = `/api/products/v2/custom-fields/${queryString ? `?${queryString}` : ''}`
      
      const response = await apiRequest.get(url)
      return {
        success: true,
        data: response.data,
        results: response.data.results || [],
        count: response.data.count || 0
      }
    } catch (error) {
      console.error('获取产品自定义字段列表失败:', error)
      throw error
    }
  },

  /**
   * 获取产品自定义字段详情
   * @param {number} fieldId - 字段ID
   * @returns {Promise<Object>} 字段详情
   */
  async getCustomField(fieldId) {
    try {
      const response = await apiRequest.get(`/api/products/v2/custom-fields/${fieldId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取产品自定义字段详情失败:', error)
      throw error
    }
  },

  /**
   * 创建产品自定义字段
   * @param {Object} fieldData - 字段数据
   * @returns {Promise<Object>} 创建结果
   */
  async createCustomField(fieldData) {
    try {
      const response = await apiRequest.post('/api/products/v2/custom-fields/', fieldData)
      return {
        success: true,
        data: response.data,
        message: '产品自定义字段创建成功'
      }
    } catch (error) {
      console.error('创建产品自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 更新产品自定义字段
   * @param {number} fieldId - 字段ID
   * @param {Object} fieldData - 更新的字段数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateCustomField(fieldId, fieldData) {
    try {
      const response = await apiRequest.put(`/api/products/v2/custom-fields/${fieldId}/`, fieldData)
      return {
        success: true,
        data: response.data,
        message: '产品自定义字段更新成功'
      }
    } catch (error) {
      console.error('更新产品自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 删除产品自定义字段
   * @param {number} fieldId - 字段ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteCustomField(fieldId) {
    try {
      await apiRequest.delete(`/api/products/v2/custom-fields/${fieldId}/`)
      return {
        success: true,
        message: '产品自定义字段删除成功'
      }
    } catch (error) {
      console.error('删除产品自定义字段失败:', error)
      throw error
    }
  },

  /**
   * 批量排序产品自定义字段
   * @param {Array<number>} fieldIds - 字段ID列表（按新顺序排列）
   * @returns {Promise<Object>} 操作结果
   */
  async sortCustomFields(fieldIds) {
    try {
      const response = await apiRequest.post('/api/products/v2/custom-fields/sort_fields/', {
        field_ids: fieldIds
      })
      return {
        success: true,
        data: response.data,
        message: response.data.message || '字段排序更新成功'
      }
    } catch (error) {
      console.error('产品自定义字段排序失败:', error)
      throw error
    }
  },

  /**
   * 获取支持的字段类型列表
   * @returns {Promise<Object>} 字段类型列表
   */
  async getFieldTypes() {
    try {
      const response = await apiRequest.get('/api/products/v2/custom-fields/field_types/')
      return {
        success: true,
        data: response.data.data || []
      }
    } catch (error) {
      console.error('获取字段类型列表失败:', error)
      throw error
    }
  }
}

// 产品列表字段配置服务
export const productFieldConfigService = {
  /**
   * 获取字段配置列表
   * @param {Object} params - 查询参数
   * @returns {Promise<Object>} API响应
   */
  async getFieldConfigs(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key])
        }
      })
      
      const queryString = queryParams.toString()
      const response = await apiRequest.get(
        `/api/products/v2/field-configs/${queryString ? `?${queryString}` : ''}`
      )
      
      return {
        success: true,
        results: response.data.results || response.data,
        count: response.data.count || (response.data.results ? response.data.results.length : 0)
      }
    } catch (error) {
      console.error('获取字段配置列表失败:', error)
      throw error
    }
  },

  /**
   * 获取单个字段配置详情
   * @param {number} configId - 配置ID
   * @returns {Promise<Object>} API响应
   */
  async getFieldConfig(configId) {
    try {
      const response = await apiRequest.get(`/api/products/v2/field-configs/${configId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取字段配置详情失败:', error)
      throw error
    }
  },

  /**
   * 创建字段配置
   * @param {Object} configData - 配置数据
   * @returns {Promise<Object>} API响应
   */
  async createFieldConfig(configData) {
    try {
      const response = await apiRequest.post('/api/products/v2/field-configs/', configData)
      return {
        success: true,
        data: response.data,
        message: '字段配置创建成功'
      }
    } catch (error) {
      console.error('创建字段配置失败:', error)
      throw error
    }
  },

  /**
   * 更新字段配置
   * @param {number} configId - 配置ID
   * @param {Object} configData - 更新数据
   * @returns {Promise<Object>} API响应
   */
  async updateFieldConfig(configId, configData) {
    try {
      const response = await apiRequest.put(`/api/products/v2/field-configs/${configId}/`, configData)
      return {
        success: true,
        data: response.data,
        message: '字段配置更新成功'
      }
    } catch (error) {
      console.error('更新字段配置失败:', error)
      throw error
    }
  },

  /**
   * 删除字段配置
   * @param {number} configId - 配置ID
   * @returns {Promise<Object>} API响应
   */
  async deleteFieldConfig(configId) {
    try {
      await apiRequest.delete(`/api/products/v2/field-configs/${configId}/`)
      return {
        success: true,
        message: '字段配置删除成功'
      }
    } catch (error) {
      console.error('删除字段配置失败:', error)
      throw error
    }
  },

  /**
   * 批量更新字段配置
   * @param {Array} fieldConfigs - 字段配置列表
   * @returns {Promise<Object>} API响应
   */
  async bulkUpdateFieldConfigs(fieldConfigs) {
    try {
      const response = await apiRequest.post('/api/products/v2/field-configs/bulk_update/', {
        field_configs: fieldConfigs
      })
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || '批量更新成功'
      }
    } catch (error) {
      console.error('批量更新字段配置失败:', error)
      throw error
    }
  },

  /**
   * 获取可见字段配置
   * @returns {Promise<Object>} API响应
   */
  async getVisibleFieldConfigs() {
    try {
      const response = await apiRequest.get('/api/products/v2/field-configs/visible_fields/')
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取可见字段配置失败:', error)
      throw error
    }
  },

  /**
   * 初始化字段配置
   * @returns {Promise<Object>} API响应
   */
  async initializeFieldConfigs() {
    try {
      const response = await apiRequest.post('/api/products/v2/field-configs/initialize_config/')
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || '初始化成功'
      }
    } catch (error) {
      console.error('初始化字段配置失败:', error)
      throw error
    }
  },

  /**
   * 获取系统字段定义
   * @returns {Promise<Object>} API响应
   */
  async getSystemFields() {
    try {
      const response = await apiRequest.get('/api/products/v2/field-configs/system_fields/')
      return {
        success: true,
        data: response.data.data || response.data
      }
    } catch (error) {
      console.error('获取系统字段定义失败:', error)
      throw error
    }
  }
}

export { productService }
export default productService
